import React, { useCallback, memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { animations } from '../../hooks/useAnimation';
import { media } from '../../styles/media';
import { Icon } from '../common/icons/IconSprite';
import { setReturnPoint } from '../common/ScrollNavigator';
import { ReactComponent as TDMLogo } from '../common/icons/TDM_light.svg';
import { Link, useNavigate } from 'react-router-dom';

const FooterWrapper = styled.footer`
  background: #2B2D42;
  color: white;
  padding: 4rem 0 2rem;
  width: 100vw;
  margin: 0;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;

  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0 8px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 4rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 16px;
    
    > div:last-child {
      padding: 0;
      margin: 0;
    }
  }

  @media (max-width: 1024px) {
    grid-template-columns: 2fr 1fr;
    gap: 3rem;
  }

  @media (max-width: 768px) {
    .quick-links-column {
      display: none !important;
    }

    > div:last-child {
      padding-left: 0;
      margin-left: 0;
    }
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
    
    &:last-child {
      margin-top: -0.5rem;
    }

    a[href^="mailto"] {
      display: none;
    }
  }
`;

const FooterLogo = styled.div`
  width: 400px;
  margin-bottom: 1rem;
  
  svg {
    width: 100%;
    height: auto;
    fill: white;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  @media (max-width: 768px) {
    width: 180px;
  }
  
  @media (max-width: 576px) {
    width: 160px;
  }
`;

const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ColumnTitle = styled.h3`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const FooterLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: left;
  font-size: 16px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  &:hover {
    color: #FF4B8F;
    transform: translateX(5px);
  }
`;

const SocialLinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-left: -4px;
  }
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 16px;
  transition: all 0.3s ease;
  
  svg {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  &:hover {
    color: #FF4B8F;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 2rem 0;

  @media (max-width: 768px) {
    margin: 1rem 24px;
  }
`;

const BottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
    padding: 0 24px;
    margin-top: 0.5rem;
  }
`;

const Copyright = styled.p`
  font-size: 0.9rem;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  a {
    color: rgba(255, 255, 255, 0.6);
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      color: #FF4B8F;
    }
  }
`;

// Общие стили
const commonTransition = css`
  transition: all 0.3s ease;
`;

const commonHoverEffect = css`
  &:hover {
    color: #FF4B8F;
  }
`;

// Мемоизированные компоненты
const SocialLinks = memo(({ t }) => (
  <SocialLinksWrapper>
    <SocialIcon 
      href="https://t.me/todealmarket" 
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon name="telegram" size={16} />
      Telegram
    </SocialIcon>
    <SocialIcon 
      href="https://x.com/todealmarket" 
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon name="twitter" size={16} />
      Twitter
    </SocialIcon>
    {/* <SocialIcon 
      href="https://www.reddit.com/user/Sad-Engine2774" 
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon name="reddit" size={16} />
      Reddit
    </SocialIcon> */}
  </SocialLinksWrapper>
));

SocialLinks.displayName = 'SocialLinks';

const QuickLinksColumn = styled(FooterColumn)`
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const FooterLinks = memo(({ onFooterClick, t }) => (
  <QuickLinksColumn>
    <ColumnTitle>{t('footer.links.title')}</ColumnTitle>
    <FooterLink onClick={onFooterClick('about')}>
      {t('footer.links.about')}
    </FooterLink>
    <FooterLink onClick={onFooterClick('roadmap')}>
      {t('footer.links.roadmap')}
    </FooterLink>
    <FooterLink onClick={onFooterClick('gifts')}>
      {t('footer.links.partners')}
    </FooterLink>
  </QuickLinksColumn>
));

FooterLinks.displayName = 'FooterLinks';

// Добавляем стили для email в мобильной версии
const EmailContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    text-align: center;
    margin: 0.5rem 0;
  }
`;

const EmailLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }

  &:hover {
    color: #FF4B8F;
  }
`;

const Footer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const handleFooterClick = useCallback((sectionId) => (e) => {
    e.preventDefault();
    setReturnPoint('footer');
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = document.querySelector('header').offsetHeight;
      const sectionPosition = section.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: sectionPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleLegalClick = useCallback((path) => (e) => {
    e.preventDefault();
    const footerPosition = document.querySelector('footer').offsetTop;
    navigate(path, { state: { fromPosition: footerPosition } });
  }, [navigate]);

  const handleLogoClick = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <FooterWrapper>
      <Icon />
      <Container>
        <FooterContent>
          <FooterColumn>
            <FooterLogo onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
              <TDMLogo />
            </FooterLogo>
            <FooterText>{t('footer.about.description')}</FooterText>
          </FooterColumn>
          
          <FooterLinks onFooterClick={handleFooterClick} t={t} />
          
          <FooterColumn>
            <ColumnTitle>{t('footer.contact.title')}</ColumnTitle>
            {/* Email будет скрыт на мобильных */}
            <FooterLink as="a" href="mailto:info@todealmarket.com">
              info@todealmarket.com
            </FooterLink>
            <SocialLinks t={t} />
          </FooterColumn>
        </FooterContent>
        
        {/* Добавляем email контейнер перед разделителем */}
        <EmailContainer>
          <EmailLink href="mailto:info@todealmarket.com">
            info@todealmarket.com
          </EmailLink>
        </EmailContainer>
        
        <Divider />
        
        <BottomBar>
          <Copyright>
            {t('footer.legal.copyright')}
          </Copyright>
          <LegalLinks>
            <FooterLink onClick={handleLegalClick('/terms')}>
              {t('footer.legal.terms')}
            </FooterLink>
            <FooterLink onClick={handleLegalClick('/privacy')}>
              {t('footer.legal.privacy')}
            </FooterLink>
          </LegalLinks>
        </BottomBar>
      </Container>
    </FooterWrapper>
  );
};

export default memo(Footer);